import { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import MuiLink from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Typography from "@mui/material/Typography";
import { startLogin, startSendEmailResetPassword } from "../../../actions/auth";
import {
  AUTH_PAGE_BRANDING_LOGO_ALT,
  AUTH_LOGIN_DEMO_EXISTING_ACCOUNT_BANNER,
  EMAIL_PLACEHOLDER,
  FIELD_EMAIL_VALID,
  FIELD_REQUIRED,
  FORGOT_PASSWORD,
  LOG_IN,
  NO_HAVE_ACCOUNT,
  PASSWORD_PLACEHOLDER,
  REGISTER_HERE,
} from "../../../i18n/keysTranslations";
import { useForm } from "react-hook-form";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { REGEX_EMAIL } from "../../../utils/regex";
import TextField from "../../../components/Form/TextField";
import { setShowBackdrop } from "../../../actions/ui";
import { useTranslationApp } from "../../../lib/i18next";
import Button from "../../../components/Buttons/Button";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";

const LoginForm = () => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
  });

  const errors = formState.errors;
  useFocusErrorForm(formState);
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isDisabledEmail, setIsDisabledEmail] = useState(false);
  const [error, setError] = useState(null);
  const [showExistingAccountBanner, setShowExistingAccountBanner] =
    useState(false);

  const { theme } = useSelectorApp((state) => state.ui);
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const emailParam = params.get("email");

  useEffect(() => {
    const redirectedEmail = location.state?.existingAccountLoginEmail;

    if (!redirectedEmail || !REGEX_EMAIL.test(String(redirectedEmail).trim())) {
      return;
    }

    setValue("email", String(redirectedEmail).trim());

    if (location.state?.fromExistingAccountRegistration === true) {
      setShowExistingAccountBanner(true);
    }

    navigate(location.pathname, { replace: true, state: {} });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (!emailParam || !REGEX_EMAIL.test(emailParam)) {
      return;
    }

    setValue("email", emailParam);
    setIsDisabledEmail(true);
    navigate("/login");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailParam]);

  const { t } = useTranslationApp();

  const forgotPasswordLinkSxBase = {
    fontWeight: "normal",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "14px",
    color: "text.primary",
    verticalAlign: "baseline",
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "transparent",
      color: "primary.main",
    },
  };

  const onChangeShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onResetPassword = async () => {
    const result = await trigger("email");

    if (!result) return;

    clearErrors();

    const formValues = getValues();
    dispatch(setShowBackdrop(true));

    await dispatch(startSendEmailResetPassword(formValues.email));

    dispatch(setShowBackdrop(false));
  };

  const onSubmit = async (formValues) => {
    setIsFetching(true);
    const response = await dispatch(
      startLogin(formValues.email, formValues.password)
    );
    setIsFetching(false);
    if (!response.ok) {
      setError(response.message);
    }
  };

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        maxWidth: 400,
        mx: "auto",
      }}
    >
      <Box sx={{ textAlign: "start" }}>
        <Avatar
          src={theme.logoLogin}
          alt={t(AUTH_PAGE_BRANDING_LOGO_ALT)}
          variant="square"
          sx={{
            width: "100px",
            height: "auto",
            maxHeight: "60px",
            ".MuiAvatar-img": {
              maxWidth: "160px",
              objectFit: "contain",
            },
          }}
        />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {showExistingAccountBanner && (
          <Alert
            severity="info"
            onClose={() => setShowExistingAccountBanner(false)}
            sx={{
              mb: 2,
              backgroundColor: "fields.background",
              color: "text.primary",
              border: `1px solid`,
              borderColor: "primary.main",
              borderRadius: BORDER_RADIUS,
              boxShadow: BOX_SHADOW_FIELDS,
              "& .MuiAlert-message": {
                width: "100%",
              },
              "& .MuiAlert-icon": {
                color: "primary.main",
              },
              "& .MuiAlert-action": {
                alignSelf: "center",
              },
            }}
          >
            {t(AUTH_LOGIN_DEMO_EXISTING_ACCOUNT_BANNER)}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "fields.background",
              color: "background.paperContrastText",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: BORDER_RADIUS,
              boxShadow: BOX_SHADOW_FIELDS,
            }}
          >
            {error}
          </Alert>
        )}

        <Typography variant="h6" color="text.primary" sx={{ fontSize: 14 }}>
          {t(EMAIL_PLACEHOLDER)}
        </Typography>

        <TextField
          margin="dense"
          fullWidth
          id="login-demo-email"
          name="email"
          placeholder="m@example.com"
          disabled={isDisabledEmail}
          autoComplete="email"
          autoFocus
          variant="filled"
          color="primary"
          sx={{
            borderRadius: 3,
            mb: 2,
            "& .MuiInputBase-input": {
              minHeight: "10px !important",
            },
          }}
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: {
              emailValid: (value) => {
                if (!REGEX_EMAIL.test(value)) {
                  return t(FIELD_EMAIL_VALID);
                }
              },
            },
          }}
          errors={errors}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" color="text.primary" sx={{ fontSize: 14 }}>
            {t(PASSWORD_PLACEHOLDER)}
          </Typography>
          <MuiLink
            component="button"
            type="button"
            underline="none"
            onClick={onResetPassword}
            sx={{
              ...forgotPasswordLinkSxBase,
              display: { xs: "none", sm: "block" },
              textAlign: "right",
            }}
          >
            {t(FORGOT_PASSWORD)}
          </MuiLink>
        </Box>
        <TextField
          margin="dense"
          fullWidth
          name="password"
          type={showPassword ? "text" : "password"}
          id="login-demo-password"
          autoComplete="current-password"
          variant="filled"
          color="primary"
          sx={{
            borderRadius: 3,
            "& .MuiInputBase-input": {
              minHeight: "10px !important",
            },
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onChangeShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <MuiLink
          component="button"
          type="button"
          underline="none"
          onClick={onResetPassword}
          sx={{
            ...forgotPasswordLinkSxBase,
            display: { xs: "block", sm: "none" },
            textAlign: "left",
          }}
        >
          {t(FORGOT_PASSWORD)}
        </MuiLink>
        <Button
          loading={isFetching}
          type="submit"
          color="primary"
          fullWidth
          variant="contained"
          sx={{
            mt: 2,
            mb: 2,
          }}
        >
          {t(LOG_IN)}
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            mt: 1,
          }}
        >
          <Typography variant="body2" align="left">
            {t(NO_HAVE_ACCOUNT)}
          </Typography>
          <Link
            to="/get-started"
            style={{
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 700,
            }}
          >
            {t(REGISTER_HERE)}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
